import React from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  Code,
  Heading,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export const AuthDebug: React.FC = () => {
  const { user, isAuthenticated, login, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const testLogin = () => {
    // Simulate a successful login with test data
    const testUser = {
      userId: 'test-user-123',
      email: 'test@example.com',
      firstName: 'Test',
      lastName: 'User',
      joinDate: new Date().toISOString(),
      isVerified: true,
      rating: 5,
      totalSales: 0,
      totalPurchases: 0,
      preferences: {
        notifications: true,
        darkMode: false,
        language: 'en',
      },
    };

    const testTokens = {
      accessToken: 'test-access-token',
      refreshToken: 'test-refresh-token',
      idToken: 'test-id-token',
    };

    login(testUser, testTokens);
    
    toast({
      title: 'Test Login Successful',
      description: 'Attempting to navigate to profile...',
      status: 'success',
      duration: 3000,
    });

    // Try navigation after a short delay
    setTimeout(() => {
      navigate('/profile');
    }, 100);
  };

  const testNavigation = () => {
    navigate('/profile');
    toast({
      title: 'Navigation Attempted',
      description: 'Tried to navigate to /profile',
      status: 'info',
      duration: 2000,
    });
  };

  const checkLocalStorage = () => {
    const accessToken = localStorage.getItem('accessToken');
    const userStr = localStorage.getItem('user');
    const refreshToken = localStorage.getItem('refreshToken');
    const idToken = localStorage.getItem('idToken');

    toast({
      title: 'LocalStorage Check',
      description: `
        AccessToken: ${accessToken ? 'Present' : 'Missing'}
        User: ${userStr ? 'Present' : 'Missing'}
        RefreshToken: ${refreshToken ? 'Present' : 'Missing'}
        IdToken: ${idToken ? 'Present' : 'Missing'}
      `,
      status: 'info',
      duration: 5000,
    });
  };

  return (
    <Box p={6} maxW="container.md" mx="auto">
      <VStack spacing={6} align="stretch">
        <Heading size="lg">Authentication Debug</Heading>

        {/* Current Auth State */}
        <Alert status={isAuthenticated ? 'success' : 'warning'}>
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontWeight="bold">
              Authentication Status: {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
            </Text>
            {user && (
              <Text fontSize="sm">
                User: {user.firstName} {user.lastName} ({user.email})
              </Text>
            )}
          </VStack>
        </Alert>

        {/* Auth State Details */}
        <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
          <Text fontWeight="bold" mb={2}>Auth Context State:</Text>
          <VStack align="start" spacing={1}>
            <Text fontSize="sm">
              <strong>isAuthenticated:</strong> <Badge colorScheme={isAuthenticated ? 'green' : 'red'}>{String(isAuthenticated)}</Badge>
            </Text>
            <Text fontSize="sm">
              <strong>user:</strong> {user ? 'Present' : 'null'}
            </Text>
            {user && (
              <Code fontSize="xs" p={2} w="full">
                {JSON.stringify(user, null, 2)}
              </Code>
            )}
          </VStack>
        </Box>

        {/* Test Actions */}
        <VStack spacing={3}>
          <HStack spacing={3} w="full">
            <Button colorScheme="blue" onClick={testLogin} flex={1}>
              Test Login
            </Button>
            <Button colorScheme="green" onClick={testNavigation} flex={1}>
              Test Navigation
            </Button>
          </HStack>
          
          <HStack spacing={3} w="full">
            <Button variant="outline" onClick={checkLocalStorage} flex={1}>
              Check LocalStorage
            </Button>
            <Button colorScheme="red" onClick={logout} flex={1}>
              Test Logout
            </Button>
          </HStack>
        </VStack>

        {/* Navigation Tests */}
        <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
          <Text fontWeight="bold" mb={2}>Quick Navigation:</Text>
          <HStack spacing={2} wrap="wrap">
            <Button size="sm" onClick={() => navigate('/')}>Home</Button>
            <Button size="sm" onClick={() => navigate('/login')}>Login</Button>
            <Button size="sm" onClick={() => navigate('/signup')}>Signup</Button>
            <Button size="sm" onClick={() => navigate('/profile')}>Profile</Button>
            <Button size="sm" onClick={() => navigate('/browse')}>Browse</Button>
          </HStack>
        </Box>

        {/* Current URL */}
        <Box p={4} bg="gray.50" borderRadius="md">
          <Text fontSize="sm">
            <strong>Current URL:</strong> {window.location.pathname}
          </Text>
          <Text fontSize="sm">
            <strong>Current Time:</strong> {new Date().toLocaleTimeString()}
          </Text>
        </Box>
      </VStack>
    </Box>
  );
}; 